import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Modal, Select, Space, Table, Typography } from 'antd';
import {
  copyRuleToUnits,
  getReturnInspectionRule,
  listProjectUnits,
} from '../../services/returnInspection';
import {
  CopyToUnitsResult,
  DecisionFlow,
  ProjectUnitRow,
  ReturnInspectionRule,
  SourceFilter,
} from '../../types/returnInspection';
import { MODE_OUTCOME } from './ruleDisplay';
import { previewLines } from './UnitRulePreview';

const { Text } = Typography;

/** 目标单元：明确勾选的 ID 列表，或者"当前筛选结果的全部"（由服务端按筛选条件圈定） */
export type BulkTargets =
  | { kind: 'ids'; ids: number[] }
  | { kind: 'filtered'; count: number; source?: SourceFilter; search?: string };

interface Props {
  open: boolean;
  projectId: number;
  targets: BulkTargets;
  onClose: () => void;
  /** 执行完成（不论有几个成功）后调用，用来刷新列表 */
  onDone: () => void;
}

/** 规则里有没有任何一处会销毁：直接销毁模式，或者检查流程里任何一个节点选了销毁 */
const ruleCanDestroy = (rule: ReturnInspectionRule): boolean => {
  if (rule.mode === 'always_destroy') return true;
  const flow = rule.decision_flow as DecisionFlow;
  if (!flow?.appearance_ok) return false;
  return [flow.appearance_ok, flow.appearance_bad].some(
    (node) => node.action === 'destroy' || node.content_bad === 'destroy',
  );
};

const BulkApplyRuleModal: React.FC<Props> = ({ open, projectId, targets, onClose, onDone }) => {
  const [sourceOptions, setSourceOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [sourceRuleId, setSourceRuleId] = useState<number | undefined>(undefined);
  const [sourceRule, setSourceRule] = useState<ReturnInspectionRule | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [destroyAck, setDestroyAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CopyToUnitsResult | null>(null);
  const [error, setError] = useState('');

  const targetCount = targets.kind === 'ids' ? targets.ids.length : targets.count;

  // 来源下拉：本项目里已经有自设规则的单元（支持按条码搜索）
  const loadSources = async (search?: string) => {
    setOptionsLoading(true);
    try {
      const data = await listProjectUnits({
        project_id: projectId,
        source: 'unit',
        page_size: 50,
        search: search || undefined,
      });
      setSourceOptions(
        data.results.map((row: ProjectUnitRow) => ({
          value: row.rule_id as number,
          label: `${row.system_barcode}　${row.mode ? MODE_OUTCOME[row.mode] : ''}`,
        })),
      );
    } catch {
      setSourceOptions([]);
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSourceRuleId(undefined);
    setSourceRule(null);
    setOverwrite(false);
    setDestroyAck(false);
    setResult(null);
    setError('');
    loadSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const chooseSource = async (ruleId: number) => {
    setSourceRuleId(ruleId);
    setSourceRule(null);
    setDestroyAck(false);
    try {
      setSourceRule(await getReturnInspectionRule(ruleId));
    } catch {
      setError('加载来源规则失败');
    }
  };

  const canDestroy = !!sourceRule && ruleCanDestroy(sourceRule);
  const canSubmit = !!sourceRule && targetCount > 0 && (!canDestroy || destroyAck) && !submitting;

  const submit = async () => {
    if (!sourceRuleId) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await copyRuleToUnits(
        targets.kind === 'ids'
          ? { source_rule_id: sourceRuleId, inventory_unit_ids: targets.ids, overwrite }
          : {
              source_rule_id: sourceRuleId,
              select_filtered: true,
              source: targets.source,
              search: targets.search,
              overwrite,
            },
      );
      setResult(res);
    } catch (e: any) {
      const data = e?.response?.data;
      setError(
        (typeof data?.detail === 'string' && data.detail) ||
          (data && typeof data === 'object'
            ? JSON.stringify(Object.values(data)[0]).replace(/[[\]"]/g, '')
            : '') ||
          '批量设置失败，请重试',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const finish = () => {
    onDone();
    onClose();
  };

  const problems = result
    ? [
        ...result.skipped.map((r) => ({ ...r, kind: '跳过' })),
        ...result.failed.map((r) => ({ ...r, kind: '失败' })),
      ]
    : [];

  return (
    <Modal
      title="批量设置规则"
      open={open}
      width={640}
      onCancel={result ? finish : onClose}
      maskClosable={false}
      footer={
        result ? (
          <Button type="primary" onClick={finish}>
            完成
          </Button>
        ) : (
          <Space>
            <Button onClick={onClose}>取消</Button>
            <Button type="primary" disabled={!canSubmit} loading={submitting} onClick={submit}>
              确认设置
            </Button>
          </Space>
        )
      }
    >
      {result ? (
        <div>
          <p style={{ marginTop: 0 }}>
            成功设置 <Text strong>{result.created.length}</Text> 个，跳过{' '}
            <Text strong>{result.skipped.length}</Text> 个，失败{' '}
            <Text strong>{result.failed.length}</Text> 个。
          </p>
          {result.content_baseline_missing > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`其中 ${result.content_baseline_missing} 个单元的流程会检查内件，但还没有内件基准资料，建议逐个补充。`}
            />
          )}
          {problems.length > 0 && (
            <Table
              size="small"
              pagination={{ pageSize: 8, hideOnSinglePage: true }}
              rowKey={(row) => `${row.kind}-${row.inventory_unit_id}`}
              dataSource={problems}
              columns={[
                {
                  title: '库存单元',
                  dataIndex: 'system_barcode',
                  render: (v, row) => v || row.inventory_unit_id,
                },
                { title: '结果', dataIndex: 'kind', width: 70 },
                { title: '原因', dataIndex: 'reason' },
              ]}
            />
          )}
        </div>
      ) : (
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="使用方法"
            description={
              <ol style={{ margin: 0, paddingLeft: 18, lineHeight: '22px' }}>
                <li>先为某一个库存单元设置好规则（在列表里点该单元的"设置规则"）。</li>
                <li>再勾选其他想使用同样规则的库存单元，点"批量设置规则"。</li>
                <li>
                  在下面选择那个已经设置好规则的库存单元作为来源，确认后，它的规则就会一次复用到所有选中的单元。
                </li>
              </ol>
            }
          />
          <div>
            将选中的 <Text strong>{targetCount}</Text>{' '}
            个库存单元，设置成和下面这个库存单元一样的规则：
          </div>
          <Select
            showSearch
            filterOption={false}
            style={{ width: '100%' }}
            placeholder="选择一个已经设置好规则的库存单元（可按条码搜索）"
            loading={optionsLoading}
            options={sourceOptions}
            value={sourceRuleId}
            onSearch={(keyword) => loadSources(keyword)}
            onChange={chooseSource}
            notFoundContent={
              optionsLoading
                ? '加载中...'
                : '本项目里还没有库存单元自设的规则，请先为某个库存单元设置规则'
            }
          />
          {sourceRule && (
            <div
              style={{ background: '#fafafa', padding: 12, borderRadius: 6, lineHeight: '22px' }}
            >
              {previewLines(sourceRule).map((line, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index}>{line}</div>
              ))}
            </div>
          )}
          <div style={{ color: '#8c8c8c', fontSize: 12 }}>
            只复制：处理方式、检查流程、吸塑/塑封开关。不复制：外观和内件的参考照片、文字说明、内件物品清单——它们对应某一种具体货物，
            请到各个库存单元里单独补充。
          </div>
          <Checkbox checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)}>
            覆盖已经有自设规则的单元（不勾选则跳过这些单元）
          </Checkbox>
          {canDestroy && (
            <Alert
              type="warning"
              showIcon
              message={`这条规则包含"直接销毁"：将批量应用到 ${targetCount} 个库存单元，符合条件的货物会被直接销毁，无法恢复。`}
              description={
                <Checkbox checked={destroyAck} onChange={(e) => setDestroyAck(e.target.checked)}>
                  我已了解，确认批量应用
                </Checkbox>
              }
            />
          )}
          {error && <Alert type="error" showIcon message={error} />}
        </Space>
      )}
    </Modal>
  );
};

export default BulkApplyRuleModal;
